import React, { useMemo } from 'react';
import { Button, Progress, Tooltip } from '@heroui/react';
import { Icon } from '@iconify/react';
import { ResponsiveTooltip, Typography } from '../../../../../components';
import { useTranslations } from '../../../../../contexts/translation.context';
import { PartnerOrdersResponse } from '../../../../../api/client/clientCplService';

const UsersGroupIcon = () => (
  <Icon
    icon="solar:users-group-two-rounded-linear"
    width={16}
    height={16}
    className="text-auth-muted"
  />
);
const CalendarIcon = () => (
  <Icon icon="solar:calendar-linear" width={16} height={16} className="text-auth-muted" />
);
const MessageIcon = () => (
  <Icon icon="solar:chat-round-line-linear" width={16} height={16} className="text-auth-muted" />
);
const EyeSlashIcon = () => (
  <Icon icon="solar:eye-closed-linear" width={24} height={24} className="text-auth-muted" />
);

const formatDate = (dateString: string | null) => {
  if (!dateString) return '—';
  // Если сервер отдает локальное время с 'Z', нужно добавить offset
  const utcDate = new Date(dateString);
  if (Number.isNaN(utcDate.getTime())) return '—';
  const date = new Date(utcDate.getTime() - utcDate.getTimezoneOffset() * 60000);
  return date.toLocaleDateString('ru-RU');
};

const formatDuration = (start: string, end: string | null) => {
  // Если сервер отдает локальное время с 'Z', нужно добавить offset
  const utcStartDate = new Date(start);
  const startDate = new Date(utcStartDate.getTime() - utcStartDate.getTimezoneOffset() * 60000);
  const utcEndDate = end ? new Date(end) : new Date();
  const endDate = end
    ? new Date(utcEndDate.getTime() - utcEndDate.getTimezoneOffset() * 60000)
    : new Date();
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return '—';
  }

  const diffMs = Math.max(endDate.getTime() - startDate.getTime(), 0);
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

  return `${diffDays}д. ${diffHours}ч.`;
};

const parseNumber = (value: string | number | null | undefined) => {
  if (value === null || value === undefined) return null;
  const numericValue = typeof value === 'string' ? Number(value) : value;
  if (Number.isNaN(numericValue)) return null;
  return numericValue;
};

const getRomiColor = (romi: number) => {
  if (romi >= 100) return 'text-auth-strength-strong';
  if (romi >= 0) return 'text-auth-strength-medium';
  if (romi > -100) return 'text-auth-strength-weak';
  return 'text-accent';
};

const buildDisplayedPages = (
  currentPage: number,
  totalPages: number
): Array<number | 'ellipsis'> => {
  const result: Array<number | 'ellipsis'> = [];
  if (totalPages <= 5) {
    for (let i = 1; i <= totalPages; i += 1) {
      result.push(i);
    }
    return result;
  }

  result.push(1);
  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);

  if (start > 2) {
    result.push('ellipsis');
  }

  for (let i = start; i <= end; i += 1) {
    result.push(i);
  }

  if (end < totalPages - 1) {
    result.push('ellipsis');
  }

  result.push(totalPages);
  return result;
};

interface SummaryCardProps {
  title: string;
  value: number;
  className?: string;
}

const SummaryCard: React.FC<SummaryCardProps> = ({ title, value, className }) => (
  <div
    className={` bg-white w-full md:w-[12.5rem] md:h-20 px-4 py-4 flex flex-col gap-4 justify-center ${className || ''}`}
  >
    <div className="h-5">
      <Typography
        variant="body2"
        className="text-sm font-medium text-auth-muted leading-[1.125rem]"
      >
        {title}
      </Typography>
    </div>
    <div className="h-6 flex items-end">
      <span className="text-2xl font-semibold text-neutral-800 leading-[normal]">{value}</span>
    </div>
  </div>
);

interface OrderItem {
  id: string;
  subscribersProgress: number;
  subscribersTarget: number;
  progressPercent: number;
  duration: string;
  dateFrom: string;
  dateTo: string;
  dialogsCount: number | null;
  dialogsPercent: number | null;
  price: number | null;
  romi: number | null;
  romiHidden?: boolean;
}

interface OrdersTableProps {
  orders: OrderItem[];
  labels: {
    subscribers: string;
    duration: string;
    dialogs: string;
    price: string;
    revenueHidden: string;
  };
}

const OrdersTable: React.FC<OrdersTableProps> = ({ orders, labels }) => (
  <div className="flex flex-col w-full">
    <div className="grid grid-cols-[200px_220px_120px_1fr] gap-x-6 items-center border-b border-[#EEEEEF] pb-4 text-xs font-normal text-auth-muted leading-4">
      <div>{labels.subscribers}</div>
      <div>{labels.duration}</div>
      {/* <div>{labels.dialogs}</div> */}
      <div className="text-right">{labels.price}</div>
      <div className="text-right">ROMI</div>
    </div>

    {orders.map((order) => (
      <div
        key={order.id}
        className="grid grid-cols-[200px_220px_120px_1fr] gap-x-6 items-center border-b border-[#EEEEEF] py-3"
      >
        <div className="flex flex-col gap-2 h-[2.375rem]">
          <Progress
            value={order.progressPercent}
            classNames={{
              base: 'w-full',
              track: 'bg-tin h-3',
              indicator: 'bg-primary',
            }}
            className="w-full"
          />
          <div className="flex items-center gap-1">
            <UsersGroupIcon />
            <span className="text-xs font-normal text-auth-muted leading-4">
              <span className="text-auth-ink">{order.subscribersProgress}</span>
              {` / ${order.subscribersTarget}`}
            </span>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-xs font-normal text-auth-ink leading-4">{order.duration}</span>
          <div className="flex items-center gap-1">
            <CalendarIcon />
            <span className="text-xs font-normal text-auth-muted leading-4">
              {order.dateFrom} - {order.dateTo}
            </span>
          </div>
        </div>

        {/* <div className="flex items-center gap-1 flex-shrink-0 w-[6.25rem]">
          <MessageIcon />
          <span className="text-xs font-normal text-auth-muted leading-4">
            {order.dialogsCount !== null ? (
              <>
                <span className="text-auth-ink">{order.dialogsCount}</span>
                {` (${order.dialogsPercent ?? 0}%)`}
              </>
            ) : (
              '—'
            )}
          </span>
        </div> */}

        <div className="text-right">
          <span className="text-sm font-semibold text-auth-ink leading-4">
            {order.price !== null ? `$${order.price.toFixed(2)}` : '—'}
          </span>
        </div>

        <div className="relative flex justify-end gap-2">
          {order.romiHidden ? (
            <ResponsiveTooltip
              content={
                <div className="px-2 py-1">
                  <p className="text-sm font-medium text-auth-ink leading-5">
                    {labels.revenueHidden}
                  </p>
                </div>
              }
              placement="top"
            >
              <div className="cursor-pointer">
                <EyeSlashIcon />
              </div>
            </ResponsiveTooltip>
          ) : (
            <>
              <span className={`text-sm font-semibold leading-4 ${getRomiColor(order.romi ?? 0)}`}>
                {order.romi !== null ? `${order.romi > 0 ? '' : ''}${order.romi}%` : '—'}
              </span>
              {/* <span className="text-xs font-normal text-auth-muted leading-4">ROMI</span> */}
            </>
          )}
        </div>
      </div>
    ))}
  </div>
);

interface PaginationControlsProps {
  displayedPages: Array<number | 'ellipsis'>;
  currentPage: number;
  totalPages: number;
  isLoading: boolean;
  onPageChange: (page: number) => void;
}

const PaginationControls: React.FC<PaginationControlsProps> = ({
  displayedPages,
  currentPage,
  totalPages,
  isLoading,
  onPageChange,
}) => (
  <div className="flex items-center rounded-2 bg-auth-surface">
    <button
      className="w-8 h-8 flex items-center justify-center disabled:opacity-30"
      onClick={() => onPageChange(currentPage - 1)}
      disabled={currentPage <= 1 || isLoading}
    >
      <Icon icon="solar:alt-arrow-left-linear" width={20} height={20} />
    </button>
    {displayedPages.map((page, index) =>
      page === 'ellipsis' ? (
        <button
          key={`ellipsis-${index}`}
          className="w-8 h-8 flex items-center justify-center text-auth-ink text-xs font-normal leading-4"
          disabled
        >
          ...
        </button>
      ) : (
        <button
          key={page}
          className={`w-8 h-8 flex items-center justify-center text-xs font-normal leading-4 rounded-2 ${
            page === currentPage
              ? 'bg-primary text-white shadow-[0px_10px_15px_-3px_rgba(150,117,206,0.3),0px_4px_6px_-2px_rgba(0,0,0,0.05)]'
              : 'text-auth-ink hover:bg-white/50'
          }`}
          onClick={() => onPageChange(page)}
          disabled={isLoading}
        >
          {page}
        </button>
      )
    )}
    <button
      className="w-8 h-8 flex items-center justify-center disabled:opacity-30"
      onClick={() => onPageChange(currentPage + 1)}
      disabled={currentPage >= totalPages || isLoading}
    >
      <Icon icon="solar:alt-arrow-right-linear" width={20} height={20} />
    </button>
  </div>
);

interface OrdersTabProps {
  onClose: () => void;
  ordersInfo: PartnerOrdersResponse | null;
  isLoading: boolean;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}

interface OrderItem {
  id: string;
  subscribersProgress: number;
  subscribersTarget: number;
  progressPercent: number;
  duration: string;
  dateFrom: string;
  dateTo: string;
  dialogsCount: number | null;
  dialogsPercent: number | null;
  price: number | null;
  romi: number | null;
  romiHidden?: boolean;
}

const OrdersTab: React.FC<OrdersTabProps> = ({
  onClose,
  ordersInfo,
  isLoading,
  currentPage,
  pageSize,
  onPageChange,
}) => {
  const { t } = useTranslations();
  const statusCounts = ordersInfo?.status_counts;

  const orderStats = {
    waiting: statusCounts?.waiting || 0,
    inProgress: statusCounts?.in_progress || 0,
    completed: statusCounts?.completed || 0,
    rejected: statusCounts?.canceled || 0,
  };

  const completedOrders: OrderItem[] = useMemo(() => {
    if (!ordersInfo?.objects) return [];

    return ordersInfo.objects.map((order) => {
      const subscribersTarget = order.subs.count;
      const subscribedCount = parseNumber(order.subscribed_count ?? null) ?? subscribersTarget;
      const dialogsCount = parseNumber(order.subs.with_gte_3_chat_messages_count ?? null);
      const dialogsPercent =
        dialogsCount !== null && subscribedCount
          ? Math.round((dialogsCount / subscribedCount) * 100)
          : null;
      const pricePerSubscriber = parseNumber(order.subs.price);
      const totalPrice = parseNumber(order.total_price);
      const income = parseNumber(order.income ?? null);
      const romi = income !== null && totalPrice ? Math.round((income / totalPrice) * 100) : null;
      const progressPercent =
        subscribersTarget > 0
          ? Math.min(Math.round((subscribedCount / subscribersTarget) * 100), 100)
          : 0;

      return {
        id: order.id,
        subscribersProgress: subscribedCount,
        subscribersTarget,
        progressPercent,
        duration: formatDuration(order.created_at, order.completed_at || order.updated_at),
        dateFrom: formatDate(order.created_at),
        dateTo: formatDate(order.completed_at || order.updated_at),
        dialogsCount,
        dialogsPercent,
        price: pricePerSubscriber,
        romi,
        romiHidden: romi === null,
      };
    });
  }, [ordersInfo]);
  const effectivePageSize = ordersInfo?.page_size || pageSize;
  const totalPages = ordersInfo ? Math.max(Math.ceil(ordersInfo.count / effectivePageSize), 1) : 1;

  const displayedPages = useMemo(
    () => buildDisplayedPages(currentPage, totalPages),
    [currentPage, totalPages]
  );

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    onPageChange(page);
  };

  const summaryCards = [
    {
      title: t('proposals.ordersDetails.waiting'),
      value: orderStats.waiting,
      className: 'max-md:border-b last:border-b-0 md:border-r border-tin',
    },
    {
      title: t('proposals.ordersDetails.inProgress'),
      value: orderStats.inProgress,
      className: 'max-md:border-b last:border-b-0',
    },
    {
      title: t('proposals.ordersDetails.completed'),
      value: orderStats.completed,
      className: 'max-md:border-b last:border-b-0 md:border-l md:border-r border-tin',
    },
    { title: t('proposals.ordersDetails.rejected'), value: orderStats.rejected },
  ];

  const tableHeader = t('proposals.ordersDetails.completedOrders');
  const tableLabels = {
    subscribers: t('proposals.ordersDetails.subscribers'),
    duration: t('proposals.ordersDetails.duration'),
    dialogs: t('proposals.ordersDetails.dialogs'),
    price: t('proposals.ordersDetails.price'),
    revenueHidden: t('proposals.ordersDetails.revenueHidden'),
  };

  return (
    <div className="flex flex-col p-4 gap-1">
      {/* Stats Cards */}
      <div className="border border-tin rounded-3.5 overflow-hidden mb-4">
        <div className="flex md:flex-row flex-col">
          {summaryCards.map((card, index) => (
            <SummaryCard key={`${card.title}-${index}`} {...card} />
          ))}
        </div>
      </div>

      {/* Completed Orders Table */}
      <div className="md:px-4 py-4 flex flex-col gap-4 items-end w-full">
        <div className="flex flex-col gap-2 w-full">
          <div className="h-6 w-full">
            <Typography variant="body2" className="font-medium text-auth-ink text-sm leading-5">
              {tableHeader}
            </Typography>
          </div>

          {completedOrders.length > 0 ? (
            <>
              <div className="md:p-0 md:m-0 -mx-4 px-4 max-md:overflow-x-auto">
                <div className="max-md:shrink-0 max-md:w-[43.75rem]">
                  <OrdersTable orders={completedOrders} labels={tableLabels} />
                </div>
              </div>
              {/* Pagination */}
              <div className="flex justify-end py-2">
                <PaginationControls
                  displayedPages={displayedPages}
                  currentPage={currentPage}
                  totalPages={totalPages}
                  isLoading={isLoading}
                  onPageChange={handlePageChange}
                />
              </div>
            </>
          ) : (
            <div className="w-full border border-tin rounded-3 py-6 px-4 flex items-center justify-center">
              <Typography variant="body2" className="text-auth-muted text-sm">
                {t('proposals.ordersDetails.noData')}
              </Typography>
            </div>
          )}
        </div>

        {/* Close Button */}
        <Button
          onPress={onClose}
          className="bg-primary text-white h-8 px-3 rounded-2 text-xs font-normal leading-4"
        >
          {t('proposals.partnerDetails.close')}
        </Button>
      </div>
    </div>
  );
};

export default OrdersTab;
